import ApplicationAdapter from './application';
import { service } from '@ember/service';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import type TasApplication from 'tio-data/models/tas-application';
import type SessionContextService from '../services/session-context';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type PcsCardHolderModel from 'tio-data/models/pcs-card-holder';
/**
 * An extended adapter for the `TasApplication` model.
 *
 * @memberof adapters
 */
class TasApplicationAdapter extends ApplicationAdapter {
  @service declare sessionContext: SessionContextService;
  @service declare demoMode: DemoModeService;
  /**
   * @param {TasApplication}  tasApplication - the TasApplication model for which to perform the action
   * @param {string} actionEndpoint - the server endpoint to append (ex. '/request-courses-approval')
   * @return {TasApplication}
   */
  async triggerActionAndReload(
    tasApplication: TasApplication,
    actionEndpoint: string,
    comment = ''
  ) {
    if (!tasApplication || !actionEndpoint) {
      return;
    }
    const url = this.buildURL('tas-application', tasApplication.id) + actionEndpoint;

    const name = this.sessionContext.user?.person?.fullName || '';
    const userId = this.sessionContext.user?.id || '';
    const currentRole = this.sessionContext.currentRole;
    const actingWithRole = {
      role: currentRole?.role,
      relationshipType: currentRole?.relationshipType,
    };
    const preApprovedTotal =
      getSingleValueForTasField('PRE_APPROVED_TOTAL', tasApplication.fields) || null;
    const finalApprovedTotal =
      getSingleValueForTasField('FINAL_APPROVED_TOTAL', tasApplication.fields) || null;
    const waiveEvidenceAttachmentRequirement =
      getSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', tasApplication.fields) ||
      false;
    const waiveEvidenceApprovalRequirement =
      getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', tasApplication.fields) ||
      false;

    await this.ajax(url, 'PATCH', {
      data: {
        note: comment,
        preApprovedTotal: preApprovedTotal,
        finalApprovedTotal: finalApprovedTotal,
        waiveEvidenceAttachmentRequirement: waiveEvidenceAttachmentRequirement,
        waiveEvidenceApprovalRequirement: waiveEvidenceApprovalRequirement,
        by: {
          name,
          userId,
          actingWithRole,
        },
      },
    });
    return await tasApplication.reload();
  }

  /**
   * @param {TasApplication} tasApplication - the tasApplication to submit for courses approval
   * @return {TasApplication}
   */

  async requestCoursesApproval(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/request-courses-approval',
      comment
    );
  }

  async approveCourses(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/approve-courses',
      comment
    );
  }

  async rejectCourses(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(tasApplication, '/transition/state/reject-courses', comment);
  }

  async requestChanges(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/decline-courses',
      comment
    );
  }

  async requestCourseEvidence(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/request-evidence-approval',
      comment
    );
  }

  async approveCourseEvidence(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/approve-evidence',
      comment
    );
  }

  async rejectCourseEvidence(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/reject-evidence',
      comment
    );
  }

  async requestCourseEvidenceChanges(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(
      tasApplication,
      '/transition/state/decline-evidence',
      comment
    );
  }

  async abandonCourse(tasApplication: TasApplication, comment = '') {
    return this.triggerActionAndReload(tasApplication, '/transition/state/abandon', comment);
  }

  // TODO: Why do we accept unkonwn here? Once this is type checked should that be handled at the call site?
  async updateApprovedTotal(
    tasApplication: TasApplication,
    amount: unknown,
    reason = '',
    additionalFieldValues?: Record<string, unknown>
  ) {
    if (!tasApplication || !reason || amount === null || amount === undefined) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/update-approved-total';
    await this.ajax(url, 'PATCH', {
      data: {
        amount,
        reason,
        additionalFieldValues,
      },
    });
    return await tasApplication.reload({
      include: 'tas-assistances',
    });
  }

  // TODO: Why do we accept unkonwn here? Once this is type checked should that be handled at the call site?
  async requestRepayment(tasApplication: TasApplication, amount: unknown, reason = '') {
    if (!tasApplication || !reason || amount === null || amount === undefined) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/request-repayment';
    await this.ajax(url, 'PATCH', {
      data: {
        amount,
        reason,
      },
    });
    return await tasApplication.reload({
      include: 'tas-assistances,tas-program-instance.employee.tas-participant',
    });
  }

  async requestPayment(tasApplication: TasApplication, amount: unknown, reason = '') {
    if (!tasApplication || !reason || amount === null || amount === undefined) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/request-payment';
    await this.ajax(url, 'PATCH', {
      data: {
        amount,
        reason,
      },
    });
    return await tasApplication.reload({
      include: 'tas-assistances,tas-program-instance.employee.tas-participant',
    });
  }

  async waiveEvidenceApproval(tasApplication: TasApplication) {
    const url = this.buildURL('tas-application', tasApplication.id) + '/waive-evidence-approval';
    await this.ajax(url, 'PATCH');
  }

  async recordRepayment(tasApplication: TasApplication, amount: number, reason = '') {
    if (!tasApplication || !reason || amount === null || amount === undefined) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/record-repayment';
    await this.ajax(url, 'PATCH', {
      data: {
        amount,
        reason,
      },
    });
    return await tasApplication.reload({
      include: 'tas-assistances,tas-program-instance.employee.tas-participant',
    });
  }

  async forgiveRepayment(tasApplication: TasApplication, amount: number, reason = '') {
    if (!tasApplication || !reason || amount === null || amount === undefined) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/forgive-repayment';
    await this.ajax(url, 'PATCH', {
      data: {
        amount,
        reason,
      },
    });
    return await tasApplication.reload({
      include: 'tas-assistances,tas-program-instance.employee.tas-participant',
    });
  }

  async cancelRepayment(tasApplication: TasApplication, reason = '') {
    if (!tasApplication || !reason) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/cancel-repayment';
    await this.ajax(url, 'PATCH', {
      data: {
        reason,
      },
    });
    return await tasApplication.reload({
      include: 'tas-assistances,tas-program-instance.employee.tas-participant',
    });
  }

  async insertReviewer(tasApplication: TasApplication, employeeId: number, ordinal: number) {
    if (!tasApplication || !employeeId || !ordinal) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/insert-reviewer';
    await this.ajax(url, 'PATCH', {
      data: {
        'employee-id': employeeId,
        ordinal,
      },
    });

    return await tasApplication.reload();
  }

  async rescindLastApproverAction(tasApplication: TasApplication) {
    const url = this.buildURL('tas-application', tasApplication.id) + '/transition/state/rescind';
    await this.ajax(url, 'PATCH');
  }

  async getHistory(tasApplication: TasApplication) {
    if (!tasApplication) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/history';
    // This custom ajax bypasses the request-manager demo handler, so consult
    // the demo provider directly (seam B) before hitting the network — the
    // approval screens load history for the demo queue applications.
    const demo = this.demoMode.demoJsonFor(url, 'GET');
    return demo !== undefined ? demo : await this.ajax(url, 'GET');
  }

  async createCardHolder(
    tasApplication: TasApplication,
    payload: {
      legal_first_name: string;
      legal_last_name: string;
      dob: string;
      agreement_id: string;
    }
  ) {
    if (
      !tasApplication ||
      !payload.legal_first_name ||
      !payload.legal_last_name ||
      !payload.dob ||
      !payload.agreement_id
    ) {
      throw new Error('Missing required parameters');
    }
    const url = this.buildURL('tas-application', tasApplication.id) + '/create-card-holder';

    const response = await this.ajax(url, 'POST', {
      data: payload,
    });
    this.store.pushPayload(response);

    const employee = tasApplication.tasProgramInstance.employee;
    const cardHolder = this.store.peekRecord<PcsCardHolderModel>(
      'pcs-card-holder',
      // @ts-expect-error: As we replace with request manager these will be resolved
      response.data.id
    );

    if (cardHolder && employee && employee.id) {
      await cardHolder.belongsTo('employee').push({ data: { type: 'employee', id: employee.id } });
    }
    return cardHolder;
  }
}
export default TasApplicationAdapter;
